/**
 * Адаптивная стратегия подключения к SSE
 * На основе истории подключений пользователя выбирает оптимальный таймаут
 */

import { addBreadcrumb, captureMessage } from '@sentry/browser';

const STORAGE_KEY = 'sse_reliability_profile';
const PROFILE_TTL_DAYS = 30; // Сбрасываем статистику через 30 дней

export type SSEReliabilityLevel = 'unknown' | 'reliable' | 'unstable' | 'problematic';

export interface SSEReliabilityProfile {
  successfulConnections: number;
  failedConnections: number;
  lastUpdate: number; // timestamp
  reliabilityLevel: SSEReliabilityLevel;
}

// Адаптивные таймауты в зависимости от уровня надежности
const TIMEOUT_BY_RELIABILITY: Record<SSEReliabilityLevel, number> = {
  reliable: 5000, // Стабильное SSE - даем больше времени
  unknown: 4000, // Нет истории - умеренный таймаут
  unstable: 2500, // Периодические проблемы - быстрее в recovery
  problematic: 1500, // Постоянные проблемы - сразу в recovery
};

// Конфигурация для последовательных ошибок SSE
export interface ConsecutiveErrorConfig {
  // Задержки reconnect в зависимости от номера попытки (в мс)
  reconnectDelays: number[];
  // Максимальное количество попыток перед переходом в recovery
  maxAttempts: number;
  // Временное окно для подсчета последовательных ошибок (в мс)
  errorWindowMs: number;
}

// Конфигурация по умолчанию для последовательных ошибок
// Упрощенная логика: 2 попытки, 3 сек задержка, потом сразу recovery
// Быстрый переход в recovery лучше чем долгое ожидание с неработающим SSE
export const DEFAULT_CONSECUTIVE_ERROR_CONFIG: ConsecutiveErrorConfig = {
  reconnectDelays: [3000], // Попытка 1 неудачна → ждём 3 сек → Попытка 2 неудачна → recovery
  maxAttempts: 2,
  errorWindowMs: 10000, // 10 секунд - окно для подсчета ошибок
};

// Адаптивная конфигурация на основе reliability level
// Упрощена: для всех уровней одинаковая стратегия (2 попытки, 3 сек)
// Различие только в том, как быстро SSE считается зависшим на CONNECTING
const CONSECUTIVE_ERROR_CONFIG_BY_RELIABILITY: Record<SSEReliabilityLevel, ConsecutiveErrorConfig> =
  {
    reliable: {
      reconnectDelays: [3000], // Даём одну попытку reconnect
      maxAttempts: 2,
      errorWindowMs: 15000,
    },
    unknown: DEFAULT_CONSECUTIVE_ERROR_CONFIG,
    unstable: DEFAULT_CONSECUTIVE_ERROR_CONFIG,
    problematic: {
      reconnectDelays: [], // Сразу в recovery без попыток reconnect
      maxAttempts: 1,
      errorWindowMs: 10000,
    },
  };

/**
 * Получить конфигурацию для последовательных ошибок на основе профиля надежности
 */
export const getConsecutiveErrorConfig = (): ConsecutiveErrorConfig => {
  const profile = getReliabilityProfile();
  return CONSECUTIVE_ERROR_CONFIG_BY_RELIABILITY[profile.reliabilityLevel];
};

/**
 * Получить задержку reconnect для конкретной попытки
 * Возвращает -1 если нужно переходить в recovery mode
 */
export const getReconnectDelayForAttempt = (attemptNumber: number): number => {
  const config = getConsecutiveErrorConfig();

  // Если превысили максимум попыток - переходим в recovery
  if (attemptNumber >= config.maxAttempts) {
    return -1; // Сигнал для перехода в recovery
  }

  const delayIndex = Math.min(attemptNumber - 1, config.reconnectDelays.length - 1);
  const delay = config.reconnectDelays[delayIndex];

  // Если delay === 0, это тоже сигнал для recovery
  if (delay === 0) {
    return -1;
  }

  return delay;
};

/**
 * Получить текущий профиль надежности из localStorage
 */
export const getReliabilityProfile = (): SSEReliabilityProfile => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return createDefaultProfile();
    }

    const profile: SSEReliabilityProfile = JSON.parse(stored);

    // Проверяем TTL
    const daysSinceUpdate = (Date.now() - profile.lastUpdate) / (1000 * 60 * 60 * 24);
    if (daysSinceUpdate > PROFILE_TTL_DAYS) {
      console.info('[SSE Strategy] Profile expired, resetting to default');
      return createDefaultProfile();
    }

    return profile;
  } catch (error) {
    console.warn('[SSE Strategy] Failed to load reliability profile:', error);
    return createDefaultProfile();
  }
};

/**
 * Создать профиль по умолчанию
 */
const createDefaultProfile = (): SSEReliabilityProfile => ({
  successfulConnections: 0,
  failedConnections: 0,
  lastUpdate: Date.now(),
  reliabilityLevel: 'unknown',
});

/**
 * Вычислить уровень надежности на основе статистики
 */
const calculateReliabilityLevel = (
  successfulConnections: number,
  failedConnections: number,
): SSEReliabilityLevel => {
  const totalConnections = successfulConnections + failedConnections;

  // Если нет истории - unknown
  if (totalConnections < 3) {
    return 'unknown';
  }

  const successRate = successfulConnections / totalConnections;

  if (successRate >= 0.8) {
    return 'reliable';
  } else if (successRate >= 0.5) {
    return 'unstable';
  } else {
    return 'problematic';
  }
};

/**
 * Сохранить профиль в localStorage
 */
const saveProfile = (profile: SSEReliabilityProfile): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch (error) {
    console.warn('[SSE Strategy] Failed to save reliability profile:', error);
  }
};

/**
 * Зарегистрировать успешное подключение SSE
 */
export const recordSuccessfulConnection = (): void => {
  const profile = getReliabilityProfile();
  const previousLevel = profile.reliabilityLevel;

  profile.successfulConnections += 1;
  profile.lastUpdate = Date.now();
  profile.reliabilityLevel = calculateReliabilityLevel(
    profile.successfulConnections,
    profile.failedConnections,
  );

  console.info('[SSE Strategy] Successful connection recorded:', {
    total: profile.successfulConnections,
    level: profile.reliabilityLevel,
  });

  // Breadcrumb для контекста в Sentry
  addBreadcrumb({
    category: 'sse.strategy',
    message: 'SSE connection successful',
    level: 'info',
    data: {
      successfulConnections: profile.successfulConnections,
      failedConnections: profile.failedConnections,
      reliabilityLevel: profile.reliabilityLevel,
    },
  });

  // Если уровень улучшился с problematic - это хорошая новость
  if (previousLevel === 'problematic' && profile.reliabilityLevel !== 'problematic') {
    captureMessage('SSE reliability improved from problematic', {
      level: 'info',
      extra: {
        previousLevel,
        newLevel: profile.reliabilityLevel,
        stats: {
          successful: profile.successfulConnections,
          failed: profile.failedConnections,
        },
      },
      tags: {
        feature: 'sse-reliability',
        event: 'level_improved',
      },
    });
  }

  saveProfile(profile);
};

/**
 * Зарегистрировать неудачное подключение (переход в recovery)
 */
export const recordFailedConnection = (): void => {
  const profile = getReliabilityProfile();
  const previousLevel = profile.reliabilityLevel;
  const isFirstFailure = profile.failedConnections === 0;

  profile.failedConnections += 1;
  profile.lastUpdate = Date.now();
  profile.reliabilityLevel = calculateReliabilityLevel(
    profile.successfulConnections,
    profile.failedConnections,
  );

  console.warn('[SSE Strategy] Failed connection recorded:', {
    total: profile.failedConnections,
    level: profile.reliabilityLevel,
  });

  // Breadcrumb для контекста в Sentry
  addBreadcrumb({
    category: 'sse.strategy',
    message: 'SSE connection failed (recovery mode)',
    level: 'warning',
    data: {
      successfulConnections: profile.successfulConnections,
      failedConnections: profile.failedConnections,
      reliabilityLevel: profile.reliabilityLevel,
      isFirstFailure,
    },
  });

  // Отправляем в Sentry при важных событиях
  if (isFirstFailure) {
    // Первый failed - важно знать
    captureMessage('SSE first connection failure detected', {
      level: 'warning',
      extra: {
        stats: {
          successful: profile.successfulConnections,
          failed: profile.failedConnections,
        },
        userAgent: navigator.userAgent,
      },
      tags: {
        feature: 'sse-reliability',
        event: 'first_failure',
      },
    });
  } else if (previousLevel !== 'problematic' && profile.reliabilityLevel === 'problematic') {
    // Уровень стал problematic - критично!
    const total = profile.successfulConnections + profile.failedConnections;
    const successRate =
      total > 0 ? ((profile.successfulConnections / total) * 100).toFixed(1) : '0';

    captureMessage('SSE reliability degraded to problematic', {
      level: 'error',
      extra: {
        previousLevel,
        stats: {
          successful: profile.successfulConnections,
          failed: profile.failedConnections,
          successRate: successRate + '%',
        },
        userAgent: navigator.userAgent,
      },
      tags: {
        feature: 'sse-reliability',
        event: 'level_problematic',
      },
    });
  }

  saveProfile(profile);
};

/**
 * Получить адаптивный таймаут подключения на основе профиля
 */
export const getAdaptiveConnectionTimeout = (): number => {
  const profile = getReliabilityProfile();
  const timeout = TIMEOUT_BY_RELIABILITY[profile.reliabilityLevel];

  console.info('[SSE Strategy] Adaptive timeout selected:', {
    reliabilityLevel: profile.reliabilityLevel,
    timeoutMs: timeout,
    stats: {
      successful: profile.successfulConnections,
      failed: profile.failedConnections,
    },
  });

  // Breadcrumb для контекста
  addBreadcrumb({
    category: 'sse.strategy',
    message: `SSE timeout: ${timeout}ms (${profile.reliabilityLevel})`,
    level: 'info',
    data: {
      timeout,
      reliabilityLevel: profile.reliabilityLevel,
      successfulConnections: profile.successfulConnections,
      failedConnections: profile.failedConnections,
    },
  });

  return timeout;
};

/**
 * Сбросить профиль надежности (для отладки или по запросу)
 */
export const resetReliabilityProfile = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    console.info('[SSE Strategy] Reliability profile reset');
  } catch (error) {
    console.warn('[SSE Strategy] Failed to reset reliability profile:', error);
  }
};

/**
 * Получить текущую статистику (для отладки)
 */
export const getReliabilityStats = (): {
  profile: SSEReliabilityProfile;
  timeout: number;
  successRate: number | null;
} => {
  const profile = getReliabilityProfile();
  const timeout = TIMEOUT_BY_RELIABILITY[profile.reliabilityLevel];
  const totalConnections = profile.successfulConnections + profile.failedConnections;
  const successRate =
    totalConnections > 0 ? (profile.successfulConnections / totalConnections) * 100 : null;

  return {
    profile,
    timeout,
    successRate,
  };
};
